import type { ITeamRepository } from "@domain/repositories";
import { TYPES } from "@utils/inversifyJS/types";
import { inject, injectable } from "inversify";
import type { CreateTeam, SoftwareEnumSchema, SoftwareVersionContextConfiguration } from "@domain/schemas/BI";
import type { ProjectMoveRequest, TeamDetail, TeamRoleName } from "@domain/schemas/workspace";
import type { IProjectRepository } from "@domain/repositories";

/**
Team detail, create/rename/delete, and membership mutations - the write half of team management, read half is `ListTeamsService`.

Also owns the target (accept/reject/pending-list) half of the project move handshake: that
authority belongs to the team being moved into, not the project being moved - see design.md's
"two distinct surfaces" decision. The source (propose/withdraw) half lives on
`ProjectManagementService`.
*/
@injectable()
export class TeamManagementService {
  @inject(TYPES.ITeamRepository)
  private _teamRepository!: ITeamRepository;

  @inject(TYPES.IProjectRepository)
  private _projectRepository!: IProjectRepository;

  async getTeamAsync(teamId: string): Promise<TeamDetail> {
    return this._teamRepository.getTeamAsync(teamId);
  }

  async createTeamAsync(request: CreateTeam): Promise<TeamDetail> {
    return this._teamRepository.createTeamAsync(request);
  }

  async renameTeamAsync(teamId: string, newName: string): Promise<TeamDetail> {
    return this._teamRepository.renameTeamAsync(teamId, newName);
  }

  async deleteTeamAsync(teamId: string): Promise<void> {
    return this._teamRepository.deleteTeamAsync(teamId);
  }

  async assignTeamRoleAsync(teamId: string, targetUserId: string, role: TeamRoleName): Promise<TeamDetail> {
    return this._teamRepository.assignTeamRoleAsync(teamId, targetUserId, role);
  }

  async removeTeamMemberAsync(teamId: string, userId: string, shouldAlsoRemoveProjectRolesInTeamProjects: boolean): Promise<TeamDetail> {
    return this._teamRepository.removeTeamMemberAsync(teamId, userId, shouldAlsoRemoveProjectRolesInTeamProjects);
  }

  async listPendingProjectMoveRequestsAsync(targetTeamId: string): Promise<ProjectMoveRequest[]> {
    return this._projectRepository.listPendingProjectMoveRequestsAsync(targetTeamId);
  }

  async acceptProjectMoveAsync(targetTeamId: string, requestId: string, newManagerUserId?: string): Promise<ProjectMoveRequest> {
    return this._projectRepository.acceptProjectMoveAsync(targetTeamId, requestId, newManagerUserId);
  }

  async rejectProjectMoveAsync(targetTeamId: string, requestId: string): Promise<ProjectMoveRequest> {
    return this._projectRepository.rejectProjectMoveAsync(targetTeamId, requestId);
  }

  async getSoftwareVersionConfigurationAsync(teamId: string): Promise<SoftwareVersionContextConfiguration> {
    return this._teamRepository.getSoftwareVersionConfigurationAsync(teamId);
  }

  async addSoftwareVersionAsync(teamId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    return this._teamRepository.addSoftwareVersionAsync(teamId, software, version);
  }

  async removeSoftwareVersionAsync(teamId: string, versionId: string): Promise<SoftwareVersionContextConfiguration> {
    return this._teamRepository.removeSoftwareVersionAsync(teamId, versionId);
  }

  async setSoftwareVersionDefaultAsync(teamId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    return this._teamRepository.setSoftwareVersionDefaultAsync(teamId, software, version);
  }

  async clearSoftwareVersionDefaultAsync(teamId: string, software: SoftwareEnumSchema): Promise<SoftwareVersionContextConfiguration> {
    return this._teamRepository.clearSoftwareVersionDefaultAsync(teamId, software);
  }
}
